import { hexA, type BarPart, type Person } from "../lib/ui";
import type { AxisDot } from "../workspace/axisDots";

/** A small solid status dot. */
export function StatusDot({ colour, size = 10 }: { colour: string; size?: number }) {
  return (
    <span
      style={{ width: size, height: size, borderRadius: 99, background: colour, display: "inline-block", flex: "none" }}
    />
  );
}

/**
 * KEY-512. One dot per scoring axis, for an item or an action point scored on more than one.
 *
 * A dot on its own can't say which axis it stands for, so every dot carries its axis and status in a tooltip
 * and the run carries the lot in one aria-label — the colours are the glance, the names are the answer.
 */
export function AxisDotRun({ dots, size = 9, max = 6, label }: {
  dots: AxisDot[];
  size?: number;
  max?: number;
  /** What this run of dots is, when it isn't self-evident — read out ahead of the axes themselves. */
  label?: string;
}) {
  if (dots.length === 0) {
    return null;
  }
  const shown = dots.slice(0, max);
  const hidden = dots.length - shown.length;
  const describe = (d: AxisDot) => `${d.label}${d.optional ? " (optional)" : ""}: ${d.statusName ?? "Not set"}`;
  return (
    <span
      role="img"
      aria-label={(label ? `${label} — ` : "") + dots.map(describe).join(", ")}
      style={{ display: "inline-flex", alignItems: "center", gap: 3, flex: "none" }}
    >
      {shown.map((d) => (
        <span
          key={d.key}
          title={describe(d)}
          style={{ width: size, height: size, borderRadius: 99, background: d.colour, display: "inline-block", flex: "none" }}
        />
      ))}
      {hidden > 0 && (
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-faint)", lineHeight: 1 }}>+{hidden}</span>
      )}
    </span>
  );
}

/** A status pill: dot + label, tinted from the status colour. */
export function StatusBadge({ colour, label, title }: { colour: string; label: string; title?: string }) {
  return (
    <span
      title={title ?? label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px 4px 8px",
        borderRadius: 99,
        fontSize: 12.5,
        fontWeight: 600,
        // KEY-529: a pill has to be the SAME HEIGHT in every row it appears in, or it cannot line up with
        // anything. Without this it inherits the row's line-height, so the identical badge came out taller
        // beside a wrapping dial name than beside a single-line one and sat off the row's optical centre.
        lineHeight: 1,
        color: colour,
        background: hexA(colour, 0.12),
        border: `1px solid ${hexA(colour, 0.26)}`,
        whiteSpace: "nowrap",
        // Never let a long name squeeze the pill: it is `nowrap`, so shrinking makes it overflow its own
        // border rather than truncate.
        flex: "none",
      }}
    >
      <StatusDot colour={colour} size={8} />
      {label}
    </span>
  );
}

/** A circular initials avatar. */
export function Avatar({ person, size = 30, ring = true }: { person: Person; size?: number; ring?: boolean }) {
  return (
    <div
      title={person.role ? `${person.name} · ${person.role}` : person.name}
      style={{
        width: size,
        height: size,
        borderRadius: 99,
        background: person.colour,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.38,
        fontWeight: 700,
        letterSpacing: "0.01em",
        flex: "none",
        boxShadow: ring ? "0 0 0 2px var(--surface)" : "none",
        fontFamily: "var(--font-ui)",
      }}
    >
      {person.initials}
    </div>
  );
}

/** Overlapping avatar stack with a "+N" overflow chip. */
export function AvatarStack({ people, size = 30, max = 4 }: { people: Person[]; size?: number; max?: number }) {
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {shown.map((p, i) => (
        <div key={i} style={{ marginLeft: i ? -size * 0.34 : 0, zIndex: shown.length - i }}>
          <Avatar person={p} size={size} />
        </div>
      ))}
      {extra > 0 && (
        <div
          style={{
            marginLeft: -size * 0.34,
            width: size,
            height: size,
            borderRadius: 99,
            background: "var(--surface-inset)",
            color: "var(--text-muted)",
            border: "var(--border-w) solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: size * 0.34,
            fontWeight: 700,
            boxShadow: "0 0 0 2px var(--surface)",
          }}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}

/** Proportional horizontal status bar. */
export function StatusBar({ parts, height = 8 }: { parts: BarPart[]; height?: number }) {
  const visible = parts.filter((p) => p.weight > 0);
  return (
    <div
      style={{
        display: "flex",
        height,
        borderRadius: 99,
        overflow: "hidden",
        background: "var(--surface-inset)",
      }}
    >
      {visible.map((p) => (
        <div
          key={p.key}
          title={p.label ? `${p.label}: ${p.weight}` : undefined}
          style={{ flex: p.weight, background: p.colour }}
        />
      ))}
    </div>
  );
}

